"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { User, Home, Users, MessageCircle } from "lucide-react";
import Image from "next/image";

type Tab = "profile" | "feed" | "connections" | "messages";

interface TabConfig {
  id: Tab;
  label: string;
  icon: React.ReactNode;
  screenshot: string;
}

const tabs: TabConfig[] = [
  {
    id: "feed",
    label: "Feed",
    icon: <Home className="w-4 h-4 sm:w-5 sm:h-5" />,
    screenshot: "/assets/pages/home/showcase/realtorsin-feed-and-profile-view.webp",
  },
  {
    id: "connections",
    label: "Connections",
    icon: <Users className="w-4 h-4 sm:w-5 sm:h-5" />,
    screenshot: "/assets/pages/home/showcase/realtorsin-connections-view.webp",
  },
  {
    id: "messages",
    label: "Messages",
    icon: <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />,
    screenshot: "/assets/pages/home/showcase/realtorsin-messages-view.webp",
  },
   {
    id: "profile",
    label: "Profile",
    icon: <User className="w-4 h-4 sm:w-5 sm:h-5" />,
    screenshot: "/assets/pages/home/showcase/realtorsin-feed-and-profile-view.webp",
  },
];

/** Client island — tab state and the screenshot that swaps with it. */
export function VisualShowcaseTabs() {
  const [activeTab, setActiveTab] = useState<Tab>("feed");
  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  return (
    <>
      {/* Tabs */}
      <div className="flex justify-center mb-8 w-full max-w-full px-2 sm:px-4">
        <div className="inline-flex bg-neutral-100 rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all duration-300 flex-shrink-0 cursor-pointer ${
                activeTab === tab.id
                  ? "bg-white shadow-lg text-primary-700"
                  : "text-neutral-800 hover:text-neutral-900"
              }`}
              aria-pressed={activeTab === tab.id}
            >
              <span
                className={`flex-shrink-0 flex items-center justify-center ${
                  activeTab === tab.id ? "text-primary-700" : "text-neutral-800"
                }`}
                aria-hidden="true"
              >
                {tab.icon}
              </span>
              <span className="font-medium text-[11px] sm:text-xs md:text-sm lg:text-base whitespace-nowrap">
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Display */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        {/* Real App Screenshots */}
        <div className="flex justify-center items-center px-4">
          <div className="relative">
            {activeTabData && (
              <Image
                src={activeTabData.screenshot}
                alt={`${activeTabData.label} Screen`}
                width={600}
                height={1200}
                className="w-full h-auto object-contain"
                sizes="(max-width: 640px) 260px, (max-width: 768px) 270px, (max-width: 1024px) 280px, 300px"
              />
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
